import type { SingleValue } from 'chakra-react-select'
import { use } from 'i18next'
import React, { useEffect, useId, useRef } from 'react'
import clsx from 'clsx'
import Select, { type MultiValue } from 'react-select'
import './ApplyV2Select.scss'

interface Option {
    value: string | number
    label?: string | null
}

interface BaseProps {
    isRequired?: boolean
    label?: string
    className?: string
    id?: string | number
    name?: string
    isDisabled?: boolean
    isLoading?: boolean
    errorMessage?: string | null | boolean | React.ReactNode
    isShowErrorMessage?: boolean
    onFocus?: React.FocusEventHandler<HTMLDivElement>
    onBlur?: React.FocusEventHandler<HTMLDivElement>
    options?: Option[]
    placeholder?: string | number
    isSearchable?: boolean
    isClearable?: boolean
    description?: React.ReactNode
    // isMulti?: boolean
}

interface SingleSelectProps extends BaseProps {
    isMulti?: false
    value?: string | number | boolean | null
    onChange?: (value: string | number | null) => void
}

interface MultiSelectProps extends BaseProps {
    isMulti: true
    value?: (string | number)[]
    onChange?: (value: (string | number)[]) => void
}

type ApplyV2SelectProps = SingleSelectProps | MultiSelectProps

const baseClass = 'ApplyV2Select'

function ApplyV2Select({
    value,
    isRequired = false,
    label,
    className,
    id,
    name,
    isDisabled = false,
    isLoading = false,
    errorMessage,
    isShowErrorMessage = true,
    onChange = () => {},
    onFocus = () => {},
    onBlur = () => {},
    options = [],
    placeholder = '',
    isClearable = false,
    isSearchable = false,
    isMulti = false,
    description,
}: ApplyV2SelectProps) {
    const selectRef = useRef(null)
    const defaultId = useId()
    const optionsWithLabels = options.map((option) => ({
        value: option.value,
        label: option.label ?? option.value.toString(),
    }))

    // const selectValue = optionsWithLabels.find((option) => option.value === value)
    const selectId = id ? id.toString() : defaultId
    const classNamePrefix = clsx(`${baseClass}__input`, {
        [`${className}__input`]: className,
    })

    const getSelectValue = () => {
        if (isMulti) {
            return optionsWithLabels.filter((opt) => (value as (string | number)[])?.includes(opt.value))
        }
        return optionsWithLabels.find((opt) => opt.value === value)
    }

    const selectValue = getSelectValue()

    const handleChange = (selectedOption: MultiValue<Option> | SingleValue<Option>) => {
        if (isMulti) {
            const values = (selectedOption as MultiValue<Option>).map((opt) => opt.value)
            ;(onChange as (value: (string | number)[]) => void)(values)
        } else {
            const value = (selectedOption as SingleValue<Option>)?.value ?? null
            ;(onChange as (value: string | number | null) => void)(value)
        }
    }

    // useEffect(() => {
    //     if (selectRef?.current && (value === '' || value === null)) {
    //         selectRef.current.clearValue()
    //     }
    // }, [value, selectRef])

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--${name}`]: name,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
                [`${baseClass}--error`]: errorMessage,
                [`${className}--disabled`]: className && isDisabled,
                [`${className}--loading`]: className && isLoading,
                [`${className}--error`]: className && errorMessage,
            })}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={name ?? selectId}>
                    {label}
                    {isRequired && '*'}
                </label>
            )}
            <Select
                ref={selectRef}
                inputId={name ?? selectId}
                id={name ?? selectId}
                name={name}
                value={selectValue}
                options={optionsWithLabels}
                placeholder={placeholder || ''}
                isClearable={isClearable}
                onChange={handleChange}
                onFocus={onFocus}
                onBlur={onBlur}
                isDisabled={isDisabled}
                isLoading={isLoading}
                isSearchable={isSearchable}
                isMulti={isMulti}
                className={clsx(`${baseClass}__input`, {
                    [`${className}__input`]: className,
                })}
                // classNamePrefix={clsx(`${baseClass}__input`, {
                //     [`${className}__input`]: className,
                // })}
                classNamePrefix={`${baseClass}__input`}
            />
            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {errorMessage && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__error`, {
                        [`${className}__error`]: className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__error--icon`, {
                            [`${className}__error--icon`]: className,
                        })}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_4800_38853)">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.00006 1.9334C4.64954 1.9334 1.9334 4.64954 1.9334 8.00006C1.9334 11.3506 4.64954 14.0667 8.00006 14.0667C11.3506 14.0667 14.0667 11.3506 14.0667 8.00006C14.0667 4.64954 11.3506 1.9334 8.00006 1.9334ZM0.733398 8.00006C0.733398 3.9868 3.9868 0.733398 8.00006 0.733398C12.0133 0.733398 15.2667 3.9868 15.2667 8.00006C15.2667 12.0133 12.0133 15.2667 8.00006 15.2667C3.9868 15.2667 0.733398 12.0133 0.733398 8.00006ZM8.00006 9.26673C7.66869 9.26673 7.40007 8.9981 7.40007 8.66673V4.66673C7.40007 4.33536 7.66869 4.06673 8.00006 4.06673C8.33144 4.06673 8.60006 4.33536 8.60006 4.66673V8.66673C8.60006 8.9981 8.33144 9.26673 8.00006 9.26673ZM8.00006 11.3334C8.36826 11.3334 8.66673 11.0349 8.66673 10.6667C8.66673 10.2985 8.36826 10.0001 8.00006 10.0001C7.63188 10.0001 7.3334 10.2985 7.3334 10.6667C7.3334 11.0349 7.63188 11.3334 8.00006 11.3334Z"
                                    fill="#B62626"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_4800_38853">
                                    <rect width="16" height="16" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                    <div
                        className={clsx(`${baseClass}__error--text`, {
                            [`${className}__error--text`]: className,
                        })}
                    >
                        {errorMessage}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2Select
